/**
 * Which recording to suggest when a Pomodoro break starts.
 *
 * Plain functions over plain data, so the choice is testable without Apollo,
 * React or the desktop shell. The hook that drives them is
 * `usePomodoroBreakSession.ts`.
 */

import { ContentTypes, DiscoverTabs, UrlSearchParams } from "types/enums";

import type { BreakSession } from "./bridge";

/** The subset of `LearnerRecording` the break query selects. */
export interface CandidateRecording {
  id: string;
  title: string;
  /** The recording's own length, e.g. "22 MINS" or "1 HR 22 MINS"; empty when unknown. */
  videoDurationText?: string | null;
  /** The scheduled class length, e.g. "45 MINS". */
  sessionDuration?: string | null;
}

/**
 * The recording's own screen: Discover with its modal open. This is where
 * the older `/user/recordings/:id` route redirects, so linking here skips a
 * deprecated hop.
 */
export function recordingRoute(id: string): string {
  const query = new URLSearchParams();
  query.set(UrlSearchParams.Tab, DiscoverTabs.SESSIONS);
  query.set(UrlSearchParams.ContentType, ContentTypes.RECORDING);
  query.set(UrlSearchParams.ContentId, id);
  return `/user/discover?${query.toString()}`;
}

/**
 * Seconds from Platform's duration text, or null when there is no number in
 * it.
 *
 * Platform writes these as "N HR(S) M MIN(S)", upper case, rounded to the
 * minute (`GroupSessionInstance.get_recording_duration_text`). Matching the
 * unit words loosely also covers "1 hr 5 min" should the casing ever change.
 */
export function parseDurationText(
  text: string | null | undefined
): number | null {
  if (text === null || text === undefined) return null;
  const hours = /(\d+)\s*HRS?\b/i.exec(text);
  const minutes = /(\d+)\s*MINS?\b/i.exec(text);
  if (hours === null && minutes === null) return null;
  const total =
    (hours === null ? 0 : Number(hours[1]) * 3600) +
    (minutes === null ? 0 : Number(minutes[1]) * 60);
  return total > 0 ? total : null;
}

/** The recording's real length when known, otherwise its class length. */
export function durationSecondsOf(
  candidate: CandidateRecording
): number | null {
  return (
    parseDurationText(candidate.videoDurationText) ??
    parseDurationText(candidate.sessionDuration)
  );
}

/**
 * The recording to suggest, or null when there is none to offer.
 *
 * 1. Only recordings with an id, a title and a known length qualify: the
 *    shell rejects a suggestion without them, and a rejected reply leaves it
 *    waiting out its timeout.
 * 2. Of those, ones no longer than the break are preferred, and among them
 *    ones not served on a recent break come first.
 * 3. Among those, the longest wins: a five-minute break is better filled by a
 *    four-minute session than a one-minute one. The id breaks a tie, so the
 *    same list always gives the same answer.
 * 4. If every fit was served recently, the one served longest ago is reused.
 * 5. If nothing fits, the shortest recording is offered instead, so the break
 *    notification still names something the learner can start.
 *
 * `recentlyServed` is oldest first, as `loadServedIds` returns it.
 */
export function selectBreakSession(
  candidates: readonly CandidateRecording[],
  breakMs: number,
  recentlyServed: readonly string[]
): BreakSession | null {
  const breakSeconds = Math.floor(breakMs / 1000);
  const usable: BreakSession[] = [];
  candidates.forEach((candidate) => {
    if (candidate.id === "" || !candidate.title) return;
    const durationSeconds = durationSecondsOf(candidate);
    if (durationSeconds === null) return;
    usable.push({
      id: candidate.id,
      title: candidate.title,
      durationSeconds,
      deepLink: recordingRoute(candidate.id),
    });
  });
  if (usable.length === 0) return null;

  const fits = usable.filter((item) => item.durationSeconds <= breakSeconds);
  if (fits.length === 0) {
    usable.sort(
      (a, b) => a.durationSeconds - b.durationSeconds || compareIds(a.id, b.id)
    );
    return usable[0];
  }

  const unseen = fits.filter((fit) => recentlyServed.indexOf(fit.id) === -1);
  if (unseen.length > 0) {
    unseen.sort(
      (a, b) => b.durationSeconds - a.durationSeconds || compareIds(a.id, b.id)
    );
    return unseen[0];
  }

  fits.sort(
    (a, b) => recentlyServed.indexOf(a.id) - recentlyServed.indexOf(b.id)
  );
  return fits[0];
}

function compareIds(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

/**
 * The recordings suggested on recent breaks, standing in for a watch history
 * Platform does not expose. Kept per origin in localStorage, like the
 * notification poller's seen ids; it is a record of suggestions, not a
 * credential.
 */
export const SERVED_IDS_KEY = "wone_desktop_pomodoro_served";

/** Enough to rotate through a working day of breaks before repeating. */
export const SERVED_IDS_LIMIT = 20;

export function loadServedIds(
  storage: Pick<Storage, "getItem"> = localStorage
): string[] {
  let raw: string | null;
  try {
    raw = storage.getItem(SERVED_IDS_KEY);
  } catch {
    // Storage blocked: no history means every fit counts as unseen, which
    // only costs a repeated suggestion.
    return [];
  }
  if (raw === null) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((id): id is string => typeof id === "string")
      : [];
  } catch {
    return [];
  }
}

/** Moves `id` to the newest end of the record and trims the oldest. */
export function recordServed(
  id: string,
  storage: Pick<Storage, "getItem" | "setItem"> = localStorage
): void {
  const next = loadServedIds(storage).filter((served) => served !== id);
  next.push(id);
  try {
    storage.setItem(
      SERVED_IDS_KEY,
      JSON.stringify(next.slice(-SERVED_IDS_LIMIT))
    );
  } catch {
    // A failed write costs a repeated suggestion on a later break, nothing more.
  }
}
